import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Plus, FileUp, Filter, Search, MoreVertical, 
  Calendar, Users, Tag, Clock, CheckCircle2,
  SlidersHorizontal, X, ChevronDown, Eye
} from 'lucide-react';

const TrackerDashboard = () => {
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const [items] = useState([
    {
      id: 1,
      name: 'Location A - System Upgrade',
      tags: [
        { text: 'IT', color: '#3B82F6' },
        { text: 'Hardware', color: '#10B981' }
      ],
      status: { text: 'Completed', color: '#10B981' },
      assignee: { name: 'John Doe', avatar: 'JD' },
      dueDate: '2024-04-01',
      lastUpdated: '2024-03-19T10:30:00',
      priority: 'High'
    },
    {
      id: 2,
      name: 'Location B - Network Setup',
      tags: [
        { text: 'IT', color: '#3B82F6' },
        { text: 'Network', color: '#8B5CF6' }
      ],
      status: { text: 'In Progress', color: '#F59E0B' },
      assignee: { name: 'Jane Smith', avatar: 'JS' },
      dueDate: '2024-04-15',
      lastUpdated: '2024-03-20T09:15:00',
      priority: 'Medium'
    },
    // Add more items as needed
  ]);

  const allTags = Array.from(new Set(items.flatMap(item => item.tags.map(tag => tag.text))));
  const allStatuses = Array.from(new Set(items.map(item => item.status.text)));

  const filteredItems = items.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesTags = selectedTags.length === 0 || 
      item.tags.some(tag => selectedTags.includes(tag.text));
    const matchesStatus = selectedStatuses.length === 0 || 
      selectedStatuses.includes(item.status.text);
    return matchesSearch && matchesTags && matchesStatus;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Tracker Items</h1>
        <div className="flex items-center space-x-3">
          <Link
            to="/tracker/import"
            className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 
              bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <FileUp className="w-4 h-4 mr-2" />
            Import List
          </Link>
          <Link
            to="/tracker/new"
            className="flex items-center px-4 py-2 text-sm font-medium text-white 
              bg-blue-600 rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Item
          </Link>
        </div>
      </div>

      {/* Search and Filter Bar */}
      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-4 space-y-4">
          <div className="flex items-center space-x-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search items..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg 
                  focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              />
            </div>
            <button 
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center px-4 py-2 text-gray-600 rounded-lg
                ${showFilters ? 'bg-blue-50 text-blue-600' : 'hover:bg-gray-50'}`}
            >
              <SlidersHorizontal className="w-4 h-4 mr-2" />
              Filters
              {(selectedTags.length > 0 || selectedStatuses.length > 0) && (
                <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-600 rounded-full">
                  {selectedTags.length + selectedStatuses.length}
                </span>
              )}
            </button>
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <div className="pt-4 border-t border-gray-100 space-y-4">
              {/* Tags Filter */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">Tags</h3>
                <div className="flex flex-wrap gap-2">
                  {allTags.map(tag => (
                    <button
                      key={tag}
                      onClick={() => {
                        setSelectedTags(prev => 
                          prev.includes(tag) 
                            ? prev.filter(t => t !== tag)
                            : [...prev, tag]
                        );
                      }}
                      className={`px-3 py-1 rounded-full text-sm font-medium
                        ${selectedTags.includes(tag)
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              </div>

              {/* Status Filter */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">Status</h3>
                <div className="flex flex-wrap gap-2">
                  {allStatuses.map(status => (
                    <button
                      key={status}
                      onClick={() => {
                        setSelectedStatuses(prev =>
                          prev.includes(status)
                            ? prev.filter(s => s !== status)
                            : [...prev, status]
                        );
                      }}
                      className={`px-3 py-1 rounded-full text-sm font-medium
                        ${selectedStatuses.includes(status)
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                    >
                      {status}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Items List */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="min-w-full divide-y divide-gray-200">
          {/* Table Header */}
          <div className="bg-gray-50">
            <div className="grid grid-cols-8 gap-4 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              <div className="col-span-2">Name</div>
              <div className="col-span-2">Tags</div>
              <div>Status</div>
              <div>Assignee</div>
              <div>Due Date</div>
              <div>Actions</div>
            </div>
          </div>

          {/* Table Body */}
          <div className="divide-y divide-gray-200 bg-white">
            {filteredItems.map(item => (
              <div key={item.id} className="grid grid-cols-8 gap-4 px-6 py-4 hover:bg-gray-50">
                <div className="col-span-2">
                  <div className="text-sm font-medium text-gray-900">{item.name}</div>
                  <div className="text-xs text-gray-500">
                    Last updated {new Date(item.lastUpdated).toLocaleDateString()}
                  </div>
                </div>
                
                <div className="col-span-2 flex gap-2">
                  {item.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                      style={{ 
                        backgroundColor: `${tag.color}15`,
                        color: tag.color
                      }}
                    >
                      {tag.text}
                    </span>
                  ))}
                </div>

                <div>
                  <span
                    className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                    style={{ 
                      backgroundColor: `${item.status.color}15`,
                      color: item.status.color
                    }}
                  >
                    {item.status.text}
                  </span>
                </div>

                <div className="flex items-center">
                  <div className="h-6 w-6 rounded-full bg-gray-200 flex items-center justify-center
                    text-xs font-medium text-gray-600">
                    {item.assignee.avatar}
                  </div>
                  <span className="ml-2 text-sm text-gray-600">{item.assignee.name}</span>
                </div>

                <div className="text-sm text-gray-600">
                  {new Date(item.dueDate).toLocaleDateString()}
                </div>

                <div className="flex items-center space-x-2">
                  <Link
                    to={`/tracker/${item.id}`}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <Eye className="w-4 h-4" />
                  </Link>
                  <button className="text-gray-400 hover:text-gray-600">
                    <MoreVertical className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrackerDashboard; 